import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import type { ServerSortColumn } from "components/Table/Table";
import Table from "components/Table/Table";
import React from "react";

import {
  getRowId,
  isServerSideSortingReport,
  computeInvoiceSelection,
} from "../utils/reportUtils";

import type { Column } from "react-data-grid";

type ReportTableProps = {
  showReportTable: boolean;
  reportColumns: Column<Record<string, unknown>>[];
  reportData: Record<string, unknown>[];
  watchedReportType: OptionType | null;
  selectedRows: Set<string | number>;
  onSelectedRowsChange?: (rows: Set<string | number>) => void;
  isLoadingReportData: boolean;
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (newPage: number) => Promise<void>;
  sortColumns: readonly ServerSortColumn[];
  onSortChange: (sortColumns: readonly ServerSortColumn[]) => void;
  reportMeta?: Record<string, unknown> | null;
  isSelectAllMode?: boolean;
  onSelectAllMode?: (value: boolean) => void;
};

const ReportTable: React.FC<ReportTableProps> = ({
  showReportTable,
  reportColumns,
  reportData,
  watchedReportType,
  selectedRows,
  onSelectedRowsChange,
  isLoadingReportData,
  page,
  pageSize,
  totalRecords,
  onPageChange,
  sortColumns,
  onSortChange,
  reportMeta,
  isSelectAllMode = false,
  onSelectAllMode,
}) => {
  if (!showReportTable || reportColumns.length === 0) {
    return null;
  }

  // enable server-side sorting when the backend supports it for this report type
  const useServerSideSorting = isServerSideSortingReport(watchedReportType);

  const isSection111 = watchedReportType?.value === "SECTION_111_INVOICING";
  const eligibleOnPage = isSection111
    ? reportData.filter(
        (row) =>
          String(
            row["Previously Invoiced"] || row.Invoiced || "",
          ).toLowerCase() !== "yes",
      ).length
    : 0;
  const allPageSelected =
    eligibleOnPage > 0 && selectedRows.size >= eligibleOnPage;
  const hasMorePages = totalRecords > reportData.length;

  return (
    <div className="report-table-container">
      {isSection111 && isSelectAllMode && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#f3e8ff",
            textAlign: "center",
            fontSize: "13px",
            borderBottom: "1px solid #e9d5ff",
          }}
        >
          <strong>All {totalRecords.toLocaleString()} items selected.</strong>{" "}
          <button
            type="button"
            onClick={() => {
              onSelectAllMode?.(false);
              onSelectedRowsChange?.(new Set());
            }}
            style={{
              background: "none",
              border: "none",
              color: "#6b21a8",
              cursor: "pointer",
              fontSize: "13px",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Clear selection
          </button>
        </div>
      )}
      {isSection111 && !isSelectAllMode && allPageSelected && hasMorePages && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#fefce8",
            textAlign: "center",
            fontSize: "13px",
            borderBottom: "1px solid #fef08a",
          }}
        >
          All {selectedRows.size} items on this page are selected.{" "}
          <button
            type="button"
            onClick={() => onSelectAllMode?.(true)}
            style={{
              background: "none",
              border: "none",
              color: "#6b21a8",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "13px",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Select all {totalRecords.toLocaleString()} items matching current
            filters
          </button>
        </div>
      )}
      <Table
        className="finance-report-table"
        columns={reportColumns}
        rows={reportData}
        rowKeyGetter={getRowId}
        onCellClick={() => {}}
        onSelectedRowsChange={
          watchedReportType?.value === "INVOICE_LIST" && onSelectedRowsChange
            ? (newSelection) => {
                const { eligibleSelection, bulkViolation, shouldDeselectAll } =
                  computeInvoiceSelection(
                    newSelection,
                    selectedRows,
                    reportData,
                  );

                if (shouldDeselectAll) {
                  onSelectedRowsChange(new Set());
                  return;
                }
                if (bulkViolation) {
                  KaboodleToaster.error(
                    "Cannot mix invoices from different bulk groups. Deselect current invoices first.",
                  );
                }
                onSelectedRowsChange(eligibleSelection);
              }
            : watchedReportType?.value === "SECTION_111_INVOICING" &&
                onSelectedRowsChange
              ? onSelectedRowsChange
              : undefined
        }
        selectedRows={
          watchedReportType?.value === "INVOICE_LIST" ||
          watchedReportType?.value === "SECTION_111_INVOICING"
            ? selectedRows
            : undefined
        }
        rowClass={(row) => {
          if (watchedReportType?.value === "INVOICE_LIST") {
            const validForPosting = String(
              row["Valid for Posting"] || "",
            ).toLowerCase();
            const status = String(row.Status || "").toLowerCase();
            const paymentStatus = String(
              row["Payment Status"] || "",
            ).toLowerCase();

            // add class for non-eligible invoices
            if (
              validForPosting !== "yes" ||
              paymentStatus === "paid" ||
              status !== "draft"
            ) {
              return "invoice-not-eligible";
            }
          }
          return undefined;
        }}
        fullHeight={false}
        variant="finance"
        stickyHeader={false}
        customScrollbars={true}
        isLoading={isLoadingReportData}
        emptyTableMessage="No data found for the selected filters"
        pagination={{
          pageSize: pageSize,
          pageIndex: page,
          rowCount: totalRecords,
          onPageChange: onPageChange,
          isLoading: isLoadingReportData,
        }}
        useServerSorting={useServerSideSorting}
        serverSorting={
          useServerSideSorting
            ? {
                sortColumns,
                onSortChange,
              }
            : undefined
        }
        reportMeta={reportMeta}
        reportType={watchedReportType?.value?.toString() || null}
      />
    </div>
  );
};

export default ReportTable;
